import { useCallback, useMemo } from 'react';

import { IPersonaFront } from '@api/routers/user/service';
import { Blockchain } from '@repo/prsn-blockchain';
import { subMinutes } from 'date-fns/subMinutes';
import { nanoid } from 'nanoid';
import superjson from 'superjson';
import { useLocalStorage } from 'usehooks-ts';
import { Hash, isHash } from 'viem';
import z from 'zod';

interface IPendingBridgeData {
  id: string;
  startedAt: Date;
  source: Blockchain;
  tokenIds: bigint[];
  stake?: boolean;
  txHash?: Hash;
}

export interface IPendingBridgeInstance {
  data: IPendingBridgeData & {
    personas: Pick<IPersonaFront, 'tokenId' | 'blockchain'>[];
  };
  setHash: (txHash: Hash) => void;
  destroy: () => void;
}

function isUpToDate(pendingBridge: Pick<IPendingBridgeData, 'startedAt'>) {
  return pendingBridge.startedAt > subMinutes(new Date(), 15);
}

const zPendingBridge = z.object({
  id: z.string(),
  startedAt: z.date(),
  source: z.nativeEnum(Blockchain),
  tokenIds: z.array(z.bigint()),
  stake: z.boolean().optional(),
  txHash: z.string().refine(isHash).optional(),
});

export function useBridgeManager() {
  const [pendingBridges, setPendingBridges] = useLocalStorage<
    IPendingBridgeData[]
  >('pending-bridges', [], {
    serializer: (pbs) => superjson.stringify(pbs.filter(isUpToDate)),
    deserializer: (valueString) => {
      try {
        const value = superjson.parse(valueString);
        return z.array(zPendingBridge).parse(value).filter(isUpToDate);
      } catch {
        return [];
      }
    },
  });

  const createInstance = useCallback(
    (params: Pick<IPendingBridgeData, 'tokenIds' | 'stake' | 'source'>) => {
      setPendingBridges((pbs) => [
        // Ensure there is only one pending bridge being created at the same time
        ...pbs.filter((p) => !p.txHash),
        {
          ...params,
          id: nanoid(5),
          startedAt: new Date(),
        },
      ]);
    },
    [],
  );

  const setHash = useCallback((txHash: Hash) => {
    setPendingBridges((pbs) => {
      const index = pbs.findIndex((p) => !p.txHash);
      const pendingBridge = pbs[index];

      if (!pendingBridge) {
        return pbs;
      }

      return [
        ...pbs.splice(0, index),
        {
          ...pendingBridge,
          txHash,
        },
        ...pbs.splice(index + 1),
      ];
    });
  }, []);

  const destroy = useCallback((id: string) => {
    setPendingBridges((pbs) => pbs.filter((p) => p.id !== id));
  }, []);

  const pendingBridgeInstances = useMemo<IPendingBridgeInstance[]>(
    () =>
      pendingBridges.map((pendingBridge) => {
        return {
          data: {
            ...pendingBridge,
            personas: pendingBridge.tokenIds.map((tokenId) => ({
              tokenId,
              blockchain: pendingBridge.source,
            })),
          },
          setHash: !pendingBridge.txHash ? setHash : () => {},
          destroy: () => destroy(pendingBridge.id),
        };
      }),
    [pendingBridges],
  );

  return {
    pendingBridges: pendingBridgeInstances,
    createInstance,
  };
}
